/**
 * Trips Section Component
 * Displays trips list within an expanded month row
 */
import React from 'react';
import { Button } from '../../components/ui/button';
import { 
  Ship, 
  Building2, 
  Home, 
  GraduationCap, 
  Palmtree, 
  TrendingUp, 
  TrendingDown, 
  Plane,
  Briefcase,
  Edit,
  ExternalLink
} from 'lucide-react';

// Get icon for trip type
const getTripIcon = (tripType, adjustmentType) => {
  switch (tripType) {
    case 'work_trip': return <Ship className="h-4 w-4 text-blue-400" />;
    case 'hotel_stay': return <Building2 className="h-4 w-4 text-amber-400" />;
    case 'uk_hotel_stay': return <Home className="h-4 w-4 text-slate-400" />;
    case 'course_uk': return <GraduationCap className="h-4 w-4 text-slate-400" />;
    case 'course_foreign': return <GraduationCap className="h-4 w-4 text-teal-400" />;
    case 'paid_leave': return <Palmtree className="h-4 w-4 text-purple-400" />;
    case 'base_work_uk': return <Briefcase className="h-4 w-4 text-slate-400" />;
    case 'base_work_foreign': return <Briefcase className="h-4 w-4 text-amber-400" />;
    case 'wage_adjustment': 
      return adjustmentType === 'deduction' 
        ? <TrendingDown className="h-4 w-4 text-red-400" />
        : <TrendingUp className="h-4 w-4 text-amber-400" />;
    case 'qualifying_holiday': return <Plane className="h-4 w-4 text-purple-400" />;
    default: return <Plane className="h-4 w-4 text-purple-400" />;
  }
};

// Get trip display name
const getTripName = (trip) => {
  if (trip.trip_type === 'paid_leave') {
    return trip.leave_type === 'annual_leave' ? 'Paid Annual Leave' : 
           trip.leave_type === 'sick_leave' ? 'Paid Sick Leave' : 'Paid Leave';
  }
  if (trip.trip_type === 'wage_adjustment') {
    return trip.adjustment_description || (trip.adjustment_type === 'deduction' ? 'Wage Deduction' : 'Wage Addition');
  }
  if (trip.trip_type === 'base_work_uk' || trip.trip_type === 'base_work_foreign') {
    return trip.base_name || 'Base Work';
  }
  if (trip.trip_type === 'qualifying_holiday') {
    return 'Qualifying Holiday';
  }
  return trip.vessel_name || trip.hotel_name || trip.course_name || 'Trip';
};

// Get trip location/subtitle
const getTripLocation = (trip) => {
  if (trip.trip_type === 'qualifying_holiday') {
    // Check foreign_ports first, then notes for location info
    if (trip.foreign_ports && trip.foreign_ports.length > 0) {
      return trip.foreign_ports.join(', ');
    }
    if (trip.notes) {
      return trip.notes;
    }
    return null;
  }
  if (trip.trip_type === 'hotel_stay' || trip.trip_type === 'uk_hotel_stay') {
    return trip.hotel_city;
  }
  if (trip.trip_type === 'course_foreign') {
    return trip.course_location;
  }
  if (trip.trip_type === 'base_work_uk' || trip.trip_type === 'base_work_foreign') {
    return trip.base_location;
  }
  if (trip.trip_type === 'work_trip') {
    return trip.contractor;
  }
  return null;
};

const TripsSection = ({ trips, onEditTrip, onViewTrip }) => {
  if (!trips || trips.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Trips</p>
      {trips.map((trip, idx) => {
        const location = getTripLocation(trip);
        
        return (
          <div 
            key={trip.id || idx} 
            className="flex items-center justify-between py-2 px-3 bg-slate-800/50 rounded-lg hover:bg-slate-700/50 transition-colors group"
            data-testid={`trip-row-${trip.id}`}
          >
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {getTripIcon(trip.trip_type, trip.adjustment_type)}
              <div className="min-w-0">
                <p className="text-sm font-medium text-white truncate">{getTripName(trip)}</p>
                {location && (
                  <p className="text-xs text-amber-400 truncate">{location}</p>
                )}
                <p className="text-xs text-slate-500">
                  {trip.overlapStart} - {trip.overlapEnd} ({trip.daysInMonth} days)
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="text-right mr-2">
                <p className="text-sm font-medium text-amber-400">
                  £{trip.monthEarnings.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                </p>
                <p className="text-xs text-slate-500">
                  {trip.pay_type === 'salaried' ? (
                    <>
                      £{(trip.monthly_salary || 0).toLocaleString('en-GB')}/mo + £{trip.dailyRate.toLocaleString('en-GB')}/day uplift
                      {trip.isPostContract && (
                        <span className="text-emerald-400 ml-1">(post-contract)</span>
                      )}
                    </>
                  ) : (
                    <>@ £{trip.dailyRate.toLocaleString('en-GB')}/day</>
                  )}
                </p>
              </div>
              {/* Action buttons - visible on hover */}
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {onEditTrip && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditTrip(trip);
                    }}
                    className="h-8 w-8 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                    title="Edit trip"
                    data-testid={`edit-trip-${trip.id}`}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                )}
                {onViewTrip && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewTrip(trip);
                    }}
                    className="h-8 w-8 text-slate-400 hover:text-white hover:bg-slate-600"
                    title="View in Trips page"
                    data-testid={`view-trip-${trip.id}`}
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default TripsSection;
